import axios, {AxiosError, HttpStatusCode} from 'axios';
import {isEmpty} from 'lodash';
import environment from '@/environment';
import AuthenticationServiceInstance from '@/services/authentication';

export const API = axios.create({
  baseURL: environment.API_BFF_URL,
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
  },
});

async function getAccessToken() {
  const accessToken = await AuthenticationServiceInstance.getAccessToken();

  if (isEmpty(accessToken)) {
    throw Error('No access token found');
  }

  return accessToken;
}

// Inject access token
API.interceptors.request.use(async request => {
  const accessToken = await getAccessToken();

  request.headers = request.headers || {};
  request.headers.Authorization = `Bearer ${accessToken}`;

  return request;
});

API.interceptors.response.use(
  response => response,
  async error => {
    if (error instanceof AxiosError && error.response?.status === HttpStatusCode.Unauthorized) {
      // Redirect to login whenever we get back a 401
      await AuthenticationServiceInstance.clearStorage();
      await AuthenticationServiceInstance.login();
      return;
    }

    return Promise.reject(error);
  },
);

// This replaces tokens like `:somethingId` in the URL with the value passed
// in `params`
API.interceptors.request.use(request => {
  const inputParams = request.params;

  if (!inputParams) {
    return request;
  }

  const inputURL = request.url;

  const {params: outputParams, url: outputURL} = Object.entries(inputParams).reduce(
    (memo, [key, value]) => {
      if (memo.url?.includes(`:${key}`)) {
        // Extract the key and keep the rest
        const {[key]: currentKey, ...rest} = memo.params;

        return {
          url: memo.url?.replaceAll(`:${key}`, String(value)),
          params: rest,
        };
      } else {
        return memo;
      }
    },
    {params: inputParams, url: inputURL},
  );

  request.url = outputURL;
  request.params = outputParams;

  return request;
});

export const Endpoints = {
  allocations: {
    collection: '/v1/allocations',
    single: '/v1/allocations/:allocationId',
  },
  investors: {
    search: '/v1/investors/search',
    single: '/v1/investors/:investorId',
    accounts: {
      single: '/v1/investors/:investorId/accounts/:accountId',
    },
  },
  definitions: {
    multiple: '/v1/configurations/value-domains',
  },
  documents: {
    clone: '/v1/documents/:documentId/clone',
    collection: '/v1/documents',
    single: '/v1/documents/:documentId',
    submitForSignature: '/v1/documents/:documentId/submit',
    retrySubmitForSignature: '/v1/documents/:documentId/submit/retry',
    cancelSignatureRequest: '/v1/documents/:documentId/signature',
    retryFinalSubmit: '/v1/documents/:documentId/finalize/retry',
    deleteSentDocuments: '/v1/documents/history/sent',
    attachments: {
      collection: '/v1/documents/:documentId/attachments',
      single: '/v1/documents/:documentId/attachments/:attachmentId',
    },
    forms: {
      generate: '/v1/documents/:documentId/forms/generate',
      single: '/v1/documents/:documentId/forms/:formId',
    },
  },
  users: {
    profile: '/v1/users/profile',
  },
};
